'use strict';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { makeStyles } from '@mui/styles';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import clsx from 'clsx';
import moment from 'moment';
import PropTypes from 'prop-types';
import ButtonWithProgress from '../../components/ButtonWithProgress.jsx';
import FirebaseConsoleLink from '../../components/FirebaseConsoleLink';
import ResponsiveDialog from '../../components/ResponsiveDialog.jsx';
import { formatValue } from '../../helpers/formatter';
import getCustomerAppURL from '../../helpers/get-customer-app-url.js';
import { pixStatusMap } from '../../helpers/pix-status';
import IssueType from '../issues/IssueType';
import { issueTypeMap } from '../issues/issue-type';
import FormCancelPix from './FormCancelPix.jsx';
import StatusIcon from './StatusIcon.jsx';
import { monitorActions } from './monitor-slice.js';

const DetailField = ({ label, value, Icon }) => (
  <Box display="flex" flexDirection="row" alignItems="center">
    <Box flexBasis="40%">
      <Typography variant="caption" color="textSecondary" component="span">
        {label} :{' '}
      </Typography>
    </Box>
    <Box flexBasis="60%">
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography variant="body1" color="textPrimary" component="span">
          {value ?? ''}
        </Typography>
        {Icon}
      </Box>
    </Box>
  </Box>
);
DetailField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  Icon: PropTypes.node,
};

const useStyles = makeStyles((theme) => ({
  clsTabs: {
    color: theme.palette.text.main,
  },
  clsSelected: {
    borderColor: theme.palette.secondary.main,
  },
  clsBatchSelected: {
    borderColor: theme.palette.error.main,
    backgroundColor: theme.palette.secondary.main,
    // borderWidth: '2px',
  },
  clsCard: {
    height: '100%',
  },
  clsMsgField: {
    backgroundColor:
      theme.palette.mode === 'light' ? 'rgba(255,255,255,0.8)' : undefined,
  },
  clsSecret: {
    backgroundColor: 'black',
    color: 'red',
  },
}));

function JourneyPix() {
  const { isSecretActive } = useSelector((state) => state.app);
  const {
    selectedPixs = [],
    selected,
    journey,
    isAwaitingCommand,
  } = useSelector((state) => state.monitor);
  const dispatch = useDispatch();
  const [showCompleted, setShowCompleted] = useState(false);
  const [isBatch, setIsBatch] = useState(false);
  const [batchSelectedPixs, setBatchSelectedPixs] = useState([]);
  const [isFormCancelPixOpen, setIsFormCancelPixOpen] = useState(false);

  const hasHiddenPixs = selectedPixs.find(
    ({ status }) =>
      status !== pixStatusMap.NO_PRAZO && status !== pixStatusMap.VENCIDA
  );
  let journeyPixs = [...selectedPixs]
    .filter(
      ({ status, issue }) =>
        showCompleted ||
        status === pixStatusMap.NO_PRAZO ||
        status === pixStatusMap.VENCIDA ||
        !!issue
    )
    .sort(
      (
        { invoiceData: { invoiceDate: a = '1900-01-01' }, createdAt: a2 },
        { invoiceData: { invoiceDate: b = '1900-01-01' }, createdAt: b2 }
      ) => {
        if (moment(a).valueOf() - moment(b).valueOf() === 0)
          return moment(a2).valueOf() - moment(b2).valueOf();
        return moment(a).valueOf() - moment(b).valueOf();
      }
    );
  const activePixs = selectedPixs
    .filter(
      ({ status, issue }) =>
        status === pixStatusMap.NO_PRAZO ||
        status === pixStatusMap.VENCIDA ||
        !!issue
    )
    .map(({ id }) => id);

  const toggleBatchSelectedPix = (id) => {
    if (batchSelectedPixs.includes(id)) {
      setBatchSelectedPixs(batchSelectedPixs.filter((pixId) => pixId !== id));
    } else {
      setBatchSelectedPixs([...batchSelectedPixs, id]);
    }
  };

  const togleBatchSelectAll = () => {
    if (batchSelectedPixs.length === activePixs.length)
      setBatchSelectedPixs([]);
    else setBatchSelectedPixs(activePixs);
  };
  const { clsSecret, clsSelected, clsBatchSelected, clsCard } = useStyles();

  return (
    <>
      <Accordion variant="outlined" defaultExpanded={true} square={false}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gap={2}
            width={1}
            paddingRight={2}
          >
            <Typography variant="body1">Cobranças em andamento</Typography>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={(e) => {
                    setIsBatch(e.target.checked);
                    if (!e.target.checked) setBatchSelectedPixs([]);
                  }}
                  checked={isBatch}
                  disabled={isAwaitingCommand['batchCancelPix']}
                />
              }
              label="Ações em lote"
            />
            <Box flexGrow={1} />
            <Button
              color="primary"
              startIcon={<OpenInNewIcon />}
              component={Link}
              onClick={(e) => {
                e.stopPropagation();
              }}
              href={`${getCustomerAppURL()}/${journey?.id}`}
              target="_blank"
              rel="noopener"
            >
              QR Code
            </Button>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {isBatch && (
            <Stack
              direction="row"
              alignItems="center"
              gap={2}
              paddingBottom={2}
            >
              <Typography>Clique nas cobranças para selecionar</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={() => togleBatchSelectAll()}
                    checked={batchSelectedPixs.length === activePixs.length}
                    disabled={isAwaitingCommand['batchCancelPix']}
                  />
                }
                label="Selecionar todas"
              />
              <ButtonWithProgress
                tooltipTitle="Cancelar"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsFormCancelPixOpen(true);
                }}
                color="primary"
                variant="contained"
                disabled={
                  batchSelectedPixs.length === 0 ||
                  isAwaitingCommand['batchCancelPix']
                }
                isLoading={isAwaitingCommand['batchCancelPix']}
                startIcon={<CancelIcon />}
              >
                Cancelar Selecionados
              </ButtonWithProgress>
            </Stack>
          )}
          <Grid container spacing={1}>
            {journeyPixs.map((pix, index) => (
              <Grid key={index} item xs={6} sm={4} md={3} lg={2} xl={2}>
                <Card
                  variant="outlined"
                  className={clsx(
                    pix.id === selected.pix.id && clsSelected,
                    batchSelectedPixs.includes(pix.id) && clsBatchSelected,
                    clsCard
                  )}
                >
                  <CardActionArea
                    className={clsCard}
                    disabled={
                      isBatch &&
                      (!activePixs.includes(pix.id) ||
                        isAwaitingCommand['batchCancelPix'])
                    }
                    onClick={() => {
                      if (isBatch) toggleBatchSelectedPix(pix.id);
                      else
                        dispatch(
                          monitorActions.setSelected({
                            pix: pix,
                            isDetailOpen: true,
                          })
                        );
                    }}
                  >
                    <Box p={1}>
                      {isSecretActive && (
                        <>
                          <Box>
                            <FirebaseConsoleLink doc={`/pix/${pix.id}`} />
                          </Box>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            className={clsSecret}
                          >
                            {pix.invoiceData.enterpriseData.pixAccount}
                          </Typography>
                        </>
                      )}
                      <DetailField
                        label="Id"
                        value={pix.invoiceData.invoiceId}
                      />
                      <DetailField
                        label="Venc"
                        value={formatValue(pix.invoiceData.invoiceDate, 'date')}
                      />
                      <DetailField
                        label="Valor"
                        value={`R$ ${formatValue(
                          parseFloat(pix.invoiceData.invoiceValue ?? 0),
                          'decimal'
                        )}`}
                      />
                      <DetailField
                        label="Status"
                        value={pix.status}
                        Icon={
                          <StatusIcon status={pix.status} fontSize="small" />
                        }
                      />
                      {pix.issue && (
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            component="span"
                          >
                            <IssueType
                              type={issueTypeMap[pix.issue.type].label}
                            />
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
            {hasHiddenPixs && (
              <Grid item xs={6} sm={4} md={3} lg={2} xl={2} container>
                <Box display="flex" alignItems="flex-end">
                  {showCompleted && (
                    <Button
                      color="primary"
                      startIcon={<VisibilityOffIcon />}
                      onClick={() => setShowCompleted(!showCompleted)}
                    >
                      Ocultar as finalizadas
                    </Button>
                  )}
                  {!showCompleted && (
                    <IconButton
                      onClick={() => setShowCompleted(!showCompleted)}
                      size="large"
                    >
                      <MoreHorizIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      <ResponsiveDialog
        open={isFormCancelPixOpen}
        maxWidth="xs"
        disableEscapeKeyDown
        fullWidth
      >
        <FormCancelPix
          onCancelClick={() => setIsFormCancelPixOpen(false)}
          onConfirmClick={(result) => {
            setIsFormCancelPixOpen(false);
            setIsBatch(false);
            setBatchSelectedPixs([]);
            dispatch(
              monitorActions.batchCancelPix({
                jid: journey?.id,
                ids: batchSelectedPixs,
                ...result,
              })
            );
          }}
        />
      </ResponsiveDialog>
    </>
  );
}

JourneyPix.propTypes = {};
export default JourneyPix;
